import type { IdpAutofillPayload } from '@euroasiann/shared/idp';
import { normalizeIdpAutofillToImport } from './rfq-import-apply';
import type {
  RfqExcelFormData,
  RfqExcelImportPreview,
  RfqExcelItem,
  RfqExcelLookupData,
} from './rfq-excel.types';
import {
  resolveRfqImportMasterData,
  type RfqImportFieldResolution,
  type RfqImportMasterLookup,
} from './rfq-import-master-resolve';
import {
  resolveRfqImportPartFinder,
  type RfqPartFinderItemResolution,
} from './rfq-import-partfinder-resolve';

export interface PrepareIdpImportResult {
  formPatch: Partial<RfqExcelFormData>;
  items: RfqExcelItem[];
  warnings: string[];
  unresolvedFields: string[];
  resolutions: RfqImportFieldResolution[];
  partfinderResolutions: RfqPartFinderItemResolution[];
  /** Preserved from IDP payload (unchanged). */
  overallConfidence: number;
  needsManualReview: boolean;
  /** Raw extracted header values (see RfqExcelImportPreview.extractedFields). */
  extractedFields?: Partial<Record<string, string>>;
}

/** Map prepared IDP result into the shared Excel/PDF import preview shape. */
export function buildIdpImportPreview(prepared: PrepareIdpImportResult): RfqExcelImportPreview {
  const matchedFields = Object.keys(prepared.formPatch).filter((k) => {
    const v = (prepared.formPatch as Record<string, unknown>)[k];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });

  return {
    formPatch: prepared.formPatch,
    items: prepared.items,
    warnings: prepared.warnings,
    errors: [],
    summary: {
      itemCount: prepared.items.length,
      vendorCount: prepared.formPatch.selectedVendorIds?.length ?? 0,
      matchedFields,
      unmatchedFields: prepared.unresolvedFields ?? [],
    },
    resolutions: prepared.resolutions,
    extractedFields: prepared.extractedFields,
    partfinderResolutions: prepared.partfinderResolutions,
    source: 'pdf',
    overallConfidence: prepared.overallConfidence,
    needsManualReview: prepared.needsManualReview,
  };
}

type IdpLookup = RfqImportMasterLookup &
  Pick<RfqExcelLookupData, 'partFinderParts' | 'getPartFinderParts' | 'vessels'>;

function resolveVesselFieldsFromLookup(
  formPatch: Partial<RfqExcelFormData> & Record<string, unknown>,
  vessels: Array<{ _id: string; name: string; imoNumber?: string }> | undefined
): {
  formPatch: Partial<RfqExcelFormData> & Record<string, unknown>;
  resolution?: RfqImportFieldResolution;
} {
  const vesselInput = String(formPatch.vesselName ?? '').trim();
  const imoInput = String(formPatch.imoNo ?? '').trim();
  const importedValue = vesselInput || imoInput;
  if (!vessels?.length) {
    return {
      formPatch,
      resolution: importedValue
        ? {
            field: 'vessel',
            importedValue,
            status: 'unresolved',
            message: `Vessel "${importedValue}" not found in fleet`,
          }
        : undefined,
    };
  }
  const next = { ...formPatch };
  const norm = (v: string) => v.toLowerCase().trim();

  const match =
    (vesselInput &&
      vessels.find(
        (v) =>
          norm(v.name) === norm(vesselInput) ||
          (v.imoNumber && norm(v.imoNumber) === norm(vesselInput))
      )) ||
    (imoInput && vessels.find((v) => v.imoNumber && norm(v.imoNumber) === norm(imoInput))) ||
    undefined;

  if (match) {
    next.vesselName = match._id;
    if (match.imoNumber) next.imoNo = match.imoNumber;
    return {
      formPatch: next,
      resolution: {
        field: 'vessel',
        importedValue,
        resolvedValue: match.name,
        resolvedId: match._id,
        status: 'matched',
      },
    };
  }
  return {
    formPatch: next,
    resolution: importedValue
      ? {
          field: 'vessel',
          importedValue,
          status: 'unresolved',
          message: `Vessel "${importedValue}" not found in fleet`,
        }
      : undefined,
  };
}

function buildIdpExtractedFields(
  payload: IdpAutofillPayload,
  normalized: Record<string, unknown>
): Record<string, string> {
  const text = (v: unknown) => (v == null ? '' : String(v).trim());
  const rfqNumberRaw = (payload.fields ?? {})['rfqNumber']?.value;
  const out: Record<string, string> = {
    rfqNumber: Array.isArray(rfqNumberRaw) ? '' : text(rfqNumberRaw),
    quoteDeadline: text(normalized.quoteDeadline),
    department: text(normalized.department),
    vessel: text(normalized.vesselName) || text(normalized.imoNo),
    category: text(normalized.category),
    subCategory: text(normalized.subCategory),
    brand: text(normalized.brand),
    model: text(normalized.model),
    vendors: text(normalized.vendors),
    port: text(normalized.portName),
    portAgent: text(normalized.portAgentName),
  };
  for (const key of Object.keys(out)) {
    if (!out[key]) delete out[key];
  }
  return out;
}

/**
 * PDF/IDP pipeline:
 * normalizeIdpAutofillToImport → vessel lookup (existing) → resolveRfqImportMasterData → resolveRfqImportPartFinder.
 */
export async function prepareIdpImportWithMasterResolve(
  payload: IdpAutofillPayload,
  lookup: IdpLookup
): Promise<PrepareIdpImportResult> {
  const normalized = normalizeIdpAutofillToImport(payload);
  const extractedFields = buildIdpExtractedFields(payload, normalized.formPatch);
  const vesselStep = resolveVesselFieldsFromLookup(normalized.formPatch, lookup.vessels);
  const withVessel = vesselStep.formPatch;

  const vendorNamesRaw =
    typeof withVessel.vendors === 'string'
      ? String(withVessel.vendors)
      : typeof (withVessel as Record<string, unknown>).vendor === 'string'
        ? String((withVessel as Record<string, unknown>).vendor)
        : '';

  const resolved = await resolveRfqImportMasterData({
    formPatch: withVessel,
    vendorNamesRaw: vendorNamesRaw || undefined,
    lookup,
  });

  let partFinderParts = lookup.partFinderParts ?? [];
  if ((!partFinderParts || partFinderParts.length === 0) && lookup.getPartFinderParts) {
    partFinderParts = await lookup.getPartFinderParts();
  }

  const partfinder = resolveRfqImportPartFinder({
    items: normalized.items,
    parts: partFinderParts,
    formPatch: resolved.formPatch,
  });

  const warnings = [...resolved.warnings, ...partfinder.warnings];

  return {
    formPatch: resolved.formPatch,
    items: partfinder.items,
    warnings,
    unresolvedFields: resolved.unresolvedFields,
    resolutions: vesselStep.resolution
      ? [vesselStep.resolution, ...resolved.resolutions]
      : resolved.resolutions,
    partfinderResolutions: partfinder.resolutions,
    overallConfidence: payload.overallConfidence,
    needsManualReview: payload.needsManualReview || warnings.length > 0,
    extractedFields,
  };
}
